import { useEffect } from 'react';

/** Set on <body> while /search shows its right rail. */
const INSET_CLASS = 'search-nav-inset';
/**
 * Set for the whole time the page is mounted, inset or not.
 *
 * The easing lives on this class rather than on INSET_CLASS: a transition
 * declared only on the inset class disappears the instant the class is removed,
 * so the header would ease IN but snap OUT. This one outlives the toggle.
 */
const PAGE_CLASS = 'search-page';

/**
 * Reserve the filter panel's column in the app header while this page is
 * mounted, then hand it back on the way out.
 *
 * The panel is a full-height right rail starting at top:0, so it shares a band
 * with TopNavigationBar. The nav is z-index 1002 against the panel's 3, and it
 * paints an opaque background across the full width of `.content` — so without
 * this the nav would paint straight over the panel's top ~73px.
 *
 * The panel is deliberately NOT raised above the nav to solve that: z-index 1003
 * would also put it above the portalled modals at 1000-1001, so a modal opened
 * from /search would render underneath the filter panel. Constraining the nav is
 * the fix; the panel's stacking stays where it is.
 *
 * Scoped to the body rather than written into the shared nav CSS, and released
 * on unmount, so no other page is affected — the same transient-override shape
 * as useCollapseNavForPage.
 *
 * Pass `enabled: false` on mobile (the panel becomes a sheet) and whenever the
 * panel is hidden, so the header reclaims the full width.
 */
export const useNavInsetForPanel = (enabled: boolean) => {
  // Marks the page for the whole mount, so the easing survives the toggle.
  useEffect(() => {
    document.body.classList.add(PAGE_CLASS);
    return () => document.body.classList.remove(PAGE_CLASS);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    document.body.classList.add(INSET_CLASS);
    return () => document.body.classList.remove(INSET_CLASS);
  }, [enabled]);
};
